import { HStack } from '@chakra-ui/react';
import { useAddress, TransactionError } from '@thirdweb-dev/react';
import React, { FC } from 'react';

import { CustomConnectWalletButton } from '@app/components/CustomConnectWalletButton';
import { ExternalLink } from '@app/components/ExternalLink';
import { NetworkMismatchButton } from '@app/components/NetworkMismatchButton';
import { SubmitButton } from '@app/components/SubmitButton';
import { showErrorToast, showSuccessToast } from '@app/components/Toast';
import { useTokenApprove } from '@app/hooks/liquidity/useTokenApprove';
import { Token } from '@app/types/token';
import { useLocationContext } from '@app/context/LocationContext';
import { useNetworkMismatch } from '@app/hooks/thirdweb/useNetworkMismatch';

interface Props {
  addLiquidityInProgress: boolean;
  onSubmit: () => void;
  areValuesEmpty: boolean;
  areTokensSelected: boolean;
  state: {
    from: {
      token: Token | null;
      amount: string;
    };
    to: {
      token: Token | null;
      amount: string;
    };
  };
  fromTokenBalance: string | undefined;
  toTokenBalance: string | undefined;
}

export const SubmitLiquidityButton: FC<Props> = ({
  state,
  addLiquidityInProgress,
  onSubmit,
  areValuesEmpty,
  areTokensSelected,
  fromTokenBalance,
  toTokenBalance
}) => {
  const { isReadOnly } = useLocationContext();
  const isMismatchedNetwork = useNetworkMismatch();
  const address = useAddress();
  const isConnected = !!address;

  const {
    requiresApproval: requiresApprovalFromToken,
    approve: approveFrom,
    isApproveInProgress: isApproveFromInProgress,
    getAllowance: getAllowanceFrom
  } = useTokenApprove(state.from);

  const {
    requiresApproval: requiresApprovalToToken,
    approve: approveTo,
    isApproveInProgress: isApproveToInProgress,
    getAllowance: getAllowanceTo
  } = useTokenApprove(state.to);

  if (isReadOnly) {
    return null;
  }

  if (!isConnected) {
    return <CustomConnectWalletButton />;
  }

  if (isMismatchedNetwork) {
    return <NetworkMismatchButton />;
  }

  if (requiresApprovalFromToken || requiresApprovalToToken) {
    return (
      <HStack width="100%">
        {requiresApprovalFromToken && (
          <SubmitButton
            isLoading={isApproveFromInProgress}
            loadingText="Processing..."
            disabled={
              !fromTokenBalance || +fromTokenBalance < +state.from.amount
            }
            onClick={async () => {
              try {
                const res = await approveFrom();

                await getAllowanceFrom();

                if (res && res['reason'] !== undefined) {
                  showErrorToast(res.reason);
                } else {
                  const txHash = res?.receipt?.transactionHash;

                  showSuccessToast(
                    `Successfully approved ${state.from.token?.symbol}`,
                    <ExternalLink txHash={txHash} />
                  );
                }
              } catch (e) {
                if (e instanceof TransactionError) {
                  showErrorToast(e.reason);
                } else {
                  showErrorToast(`Error approving ${state.from.token?.symbol}`);
                }
              }
            }}
            label={`Approve ${state.from.token?.symbol}`}
          />
        )}
        {requiresApprovalToToken && (
          <SubmitButton
            isLoading={isApproveToInProgress}
            loadingText="Processing..."
            disabled={!toTokenBalance || +toTokenBalance < +state.to.amount}
            onClick={async () => {
              try {
                const res = await approveTo();

                await getAllowanceTo();

                if (res['reason'] !== undefined) {
                  showErrorToast(res.reason);
                } else {
                  showSuccessToast(
                    `Successfully approved ${state.to.token?.symbol}`
                  );
                }
              } catch (e) {
                if (e instanceof TransactionError) {
                  showErrorToast(e.reason);
                } else {
                  showErrorToast(`Error approving ${state.to.token?.symbol}`);
                }
              }
            }}
            label={`Approve ${state.to.token?.symbol}`}
          />
        )}
      </HStack>
    );
  }

  if (!isMismatchedNetwork) {
    return (
      <SubmitButton
        isLoading={addLiquidityInProgress}
        disabled={areValuesEmpty || !areTokensSelected}
        loadingText="Processing..."
        onClick={onSubmit}
        label={
          areValuesEmpty || !areTokensSelected
            ? 'Select tokens and enter amounts'
            : 'Proceed to add liquidity'
        }
      />
    );
  }

  return null;
};
